export type PreviewTool = 'concept' | 'impact' | 'wayfinder';

export type PreviewLocation = {
  label: string;
  city?: string;
  region?: string;
  country?: string;
};

export type PreviewPlatformFilter = {
  socialMedia: boolean;
  professional: boolean;
  government: boolean;
  news: boolean;
};

export type PreviewIntent = {
  tool: PreviewTool;
  idea: string;
  returnPath: string;
  locationOverride?: PreviewLocation | null;
  locationLabel?: string | null;
  businessFocus?: 'local' | 'regional' | 'national' | 'global' | 'flexible';
  manualLocation?: string;
  platformFilter?: PreviewPlatformFilter;
  selectedIndustry?: string;
  wayfinderFocus?: string[];
  createdAt: number;
};

const STORAGE_KEY = 'pendingToolPreviewIntent';
export const PREVIEW_INTENT_TTL_MS = 1000 * 60 * 60 * 24;

const TOOL_PATHS: Record<PreviewTool, string> = {
  concept: '/concept-validator',
  impact: '/impact-evaluator',
  wayfinder: '/wayfinder',
};

const TOOL_SOURCES: Record<PreviewTool, string> = {
  concept: 'concept-validator',
  impact: 'impact-evaluator',
  wayfinder: 'wayfinder',
};

const isFresh = (createdAt?: number) => {
  if (typeof createdAt !== 'number') return false;
  const age = Date.now() - createdAt;
  return age >= 0 && age < PREVIEW_INTENT_TTL_MS;
};

const clearStoredPreviewState = (intent?: Partial<PreviewIntent> | null) => {
  window.localStorage.removeItem(STORAGE_KEY);
  if (!intent?.idea || window.localStorage.getItem('pendingIdea') === intent.idea) {
    window.localStorage.removeItem('pendingIdea');
  }
  if (!intent?.tool || window.localStorage.getItem('pendingIdeaFrom') === intent.tool) {
    window.localStorage.removeItem('pendingIdeaFrom');
  }
  if (!intent?.returnPath || window.sessionStorage.getItem('redirectAfterLogin') === intent.returnPath) {
    window.sessionStorage.removeItem('redirectAfterLogin');
  }
};

export const getPreviewToolFromSource = (source?: string | null): PreviewTool | null => {
  const normalized = String(source || '')
    .trim()
    .replace(/^\//, '')
    .split(/[?#]/)[0]
    .toLowerCase();

  if (normalized === 'get-started' || normalized === 'concept-validator' || normalized === 'concept') {
    return 'concept';
  }
  if (normalized === 'impact-evaluator' || normalized === 'impact') return 'impact';
  if (normalized === 'wayfinder') return 'wayfinder';
  return null;
};

export const buildPreviewResumePath = (tool: PreviewTool, idea: string) => {
  const params = new URLSearchParams({ autoRun: '1', idea: idea.trim() });
  return `${TOOL_PATHS[tool]}?${params.toString()}`;
};

export const buildPreviewAuthPath = (
  destination: 'login' | 'signup',
  tool: PreviewTool,
  idea: string,
) => {
  const params = new URLSearchParams({
    idea: idea.trim(),
    from: TOOL_SOURCES[tool],
    intent: 'report',
  });
  return `/${destination}?${params.toString()}`;
};

export const savePreviewIntent = (intent: Omit<PreviewIntent, 'createdAt'>) => {
  if (typeof window === 'undefined') return;
  try {
    const payload: PreviewIntent = { ...intent, createdAt: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.localStorage.setItem('pendingIdea', intent.idea);
    window.localStorage.setItem('pendingIdeaFrom', intent.tool);
    window.sessionStorage.setItem('redirectAfterLogin', intent.returnPath);
  } catch (_) {
    /* Storage can be unavailable in private browsing; callers still show preview. */
  }
};

export const readPreviewIntent = (tool?: PreviewTool): PreviewIntent | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PreviewIntent>;
    const hasValidTool = parsed.tool === 'concept' || parsed.tool === 'impact' || parsed.tool === 'wayfinder';
    if (
      !hasValidTool ||
      typeof parsed.idea !== 'string' ||
      !parsed.idea.trim() ||
      typeof parsed.returnPath !== 'string' ||
      !isFresh(parsed.createdAt)
    ) {
      clearStoredPreviewState(parsed);
      return null;
    }
    if (tool && parsed.tool !== tool) return null;
    return parsed as PreviewIntent;
  } catch (_) {
    clearStoredPreviewState();
    return null;
  }
};

export const clearPreviewIntent = (tool?: PreviewTool) => {
  if (typeof window === 'undefined') return;
  try {
    const current = readPreviewIntent();
    if (!tool || current?.tool === tool) {
      clearStoredPreviewState(current);
    }
  } catch (_) {
    /* non-fatal */
  }
};

export const clearPreviewIntentAfterPersist = (
  tool: PreviewTool,
  reportId?: number | null,
) => {
  if (!Number.isInteger(reportId) || Number(reportId) <= 0) return false;
  clearPreviewIntent(tool);
  return true;
};
